import { useMemo } from "react";

import { BxpWebStargateClientProvider } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";

import { RoadmapSubscriptionForm } from "../../roadmap-subscribtion-form/src/roadmap-subscription-form.tsx";

import { RoadmapFeaturesQueryResult, RoadmapName } from "./client";
import { useQuery } from "./client/hook.ts";
import { Cards } from "./components/cards.tsx";
import { ColumnHeaders } from "./components/column-headers.tsx";
import { Layout } from "./components/layout.tsx";
import { RoadmapSubNav } from "./components/roadmap-subnav.tsx";
import { SearchWithFilters } from "./components/search-with-filters";
import {
    RoadmapFeaturesContextProvider,
    CLOUD_BASE_PATH,
    emptyFilters
} from "./context/context.tsx";
import { getQueryParamFromWindow, getSlugFromWindow } from "./context/url";
import {
    RoadmapFeatureFilters,
    SortCriteria,
    SelectedRoadmapFeature,
    SortKey,
    SortDirection,
    FilterOptionsByKey
} from "./types";

const pageBasePath = CLOUD_BASE_PATH;

export interface UniformRoadmapFeaturesProps {
    roadmapFeaturesLocale: string;
    roadmapName: RoadmapName;
    data: RoadmapFeaturesQueryResult;
}

const UniformRoadmapFeatures = (props: UniformRoadmapFeaturesProps) => {
    const { data: ssrData, roadmapFeaturesLocale, roadmapName } = props;

    const { filters, sortCriteria, selectedFeature } = useMemo(() => {
        if (typeof window === "undefined") {
            return {
                filters: emptyFilters,
                sortCriteria: undefined,
                selectedFeature: undefined
            };
        }

        const filters: RoadmapFeatureFilters = {
            category: getQueryParamFromWindow("category"),
            search: getQueryParamFromWindow("search"),
            status: getQueryParamFromWindow("status"),
            selectedProduct: getQueryParamFromWindow("selectedProduct")
        };

        const sortCriteria: SortCriteria = {
            key: getQueryParamFromWindow("sortBy")[0] as SortKey,
            direction: getQueryParamFromWindow("sort")[0] as SortDirection
        };

        let selectedFeature: SelectedRoadmapFeature | undefined;
        const slug = getSlugFromWindow({ basePath: pageBasePath });
        if (slug) {
            selectedFeature = {
                pid: getQueryParamFromWindow("p")?.[0],
                slug
            };
        }

        return {
            filters,
            sortCriteria,
            selectedFeature
        };
    }, []);

    const { result: csrData } = useQuery({
        locale: roadmapFeaturesLocale,
        roadmapName
    });

    const features =
        csrData?.roadmapFeatureCollection?.items ??
        ssrData?.roadmapFeatureCollection?.items;

    const filterOptionsByKey: FilterOptionsByKey = {
        category:
            ssrData?.taxonomyRoadmapCategoryCollection.items.map((item) => ({
                value: item.queryParameter,
                label: item.roadmapCategoryNameExternal
            })) ?? [],
        selectedProduct:
            ssrData?.taxonomyRoadmapAppCollection.items.map((item) => ({
                value: item.filterQueryParam,
                label: item.roadmapAppNameExternal
            })) ?? [],
        status:
            ssrData?.taxonomyRoadmapStatusCollection.items.map((item) => ({
                value: item.roadmapStatusNameExternal,
                label: item.roadmapStatusNameExternal
            })) ?? []
    };

    return (
        <BxpWebStargateClientProvider
            baseUrl={process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"] as string}
        >
            <RoadmapFeaturesContextProvider
                pageBasePath={pageBasePath}
                filters={filters}
                sortCriteria={sortCriteria}
                selectedFeature={selectedFeature}
                features={features}
                filterOptionsByKey={filterOptionsByKey}
            >
                {/* this div is necessary to prevent unnwanted spacing because the parent container 
                    is a column layout which has inherent gap spacing */}
                <div>
                    <RoadmapSubNav />
                    <RoadmapSubscriptionForm />
                    <SearchWithFilters />
                    <Layout
                        columnHeaders={<ColumnHeaders />}
                        cards={<Cards />}
                    />
                </div>
            </RoadmapFeaturesContextProvider>
        </BxpWebStargateClientProvider>
    );
};

export default UniformRoadmapFeatures;
